import { NavLink } from 'react-router-dom';
import { usePolledQuery } from '../hooks/usePolledQuery';
import { getExpertBacklogAlertCount } from '../utils/expertConsultationBacklog';

const ENV_LABEL =
  import.meta.env.VITE_ENV_LABEL || (import.meta.env.DEV ? 'LOCAL' : '');
const NAV_ITEMS = [
  { label: 'Dashboard', path: '/dashboard', icon: '⌂' },
  { label: 'Live', path: '/live', icon: '◉' },
  { label: 'Jobs', path: '/jobs', icon: '⚙' },
  { label: 'Queues', path: '/queues', icon: '≡' },
  { label: 'Cost', path: '/cost', icon: '$' },
  { label: 'Health Checks', path: '/health-checks', icon: '♥' },
  { label: 'Analytics', path: '/analytics', icon: '📊' },
  { label: 'Moderation', path: '/moderation', icon: '⚑' },
  { label: 'Post Moderation', path: '/moderation/posts', icon: '🚩' },
  { label: 'Feed producers', path: '/feed/producers', icon: '⚡' },
  { label: 'Business Agents', path: '/agents', icon: '✦' },
  { label: 'Providers', path: '/providers', icon: '🛠' },
  { label: 'Experts', path: '/experts', icon: '🎓' },
  { label: 'Knowledge', path: '/knowledge', icon: '📚' },
  { label: 'Users', path: '/users', icon: '👥' },
  { label: 'Review Outcomes', path: '/review-outcomes', icon: '✅' },
  { label: 'Review Queue', path: '/review-queue', icon: '⏳' },
  { label: 'Shared record review', path: '/shared-records', icon: '▤' },
  { label: 'AI Config', path: '/config', icon: '⚙' },
  { label: 'Templates', path: '/templates', icon: '✉' },
  { label: 'Task Templates', path: '/task-templates', icon: '☑' },
  { label: 'Categories', path: '/categories', icon: '🗂' },
  { label: 'Audit Log', path: '/audit', icon: '⧉' },
] as const;

// Reports group (spec 016) — later waves append their report pages here.
const REPORT_NAV_ITEMS = [
  { label: 'Onboarding Funnel', path: '/reports/onboarding', icon: '⏷' },
  { label: 'Activation Funnel', path: '/reports/activation', icon: '★' },
  { label: 'Boards', path: '/reports/boards', icon: '▦' },
  { label: 'AI Generation', path: '/reports/ai-generation', icon: '✧' },
  { label: 'Engagement', path: '/reports/engagement', icon: '⇄' },
  { label: 'Billing', path: '/reports/billing', icon: '¤' },
  { label: 'Usage', path: '/reports/usage', icon: '∿' },
  { label: 'User Lookup', path: '/reports/users', icon: '⌕' },
] as const;

function NavItem({ label, path, icon, badge }: { label: string; path: string; icon: string; badge?: number }) {
  return (
    <NavLink
      to={path}
      aria-label={badge ? `${label} — ${badge} unassigned past estimated response time` : label}
      className={({ isActive }) =>
        [
          'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors min-h-[48px]',
          isActive
            ? 'bg-violet-700 text-white'
            : 'text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100',
        ].join(' ')
      }
    >
      <span aria-hidden="true" className="text-base w-5 text-center">{icon}</span>
      <span className="flex-1">{label}</span>
      {!!badge && (
        <span
          className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/20 text-amber-400 border border-amber-500/30"
          data-testid={`nav-badge-${path.slice(1)}`}
        >
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </NavLink>
  );
}

export function Sidebar({ open = false, onNavigate }: { open?: boolean; onNavigate?: () => void }) {
  // Requested consultations sitting unassigned past their own EstimatedResponseTime
  // (ADO 15202) — polled rather than fetched once since the sidebar stays mounted
  // for the whole admin session and this count can change without a navigation.
  const { data: expertBacklogAlertCount } = usePolledQuery(getExpertBacklogAlertCount, 60_000);

  return (
    <nav
      id="main-navigation"
      aria-label="Main navigation"
      onClick={onNavigate}
      className={[
        'flex-col w-56 shrink-0 min-h-screen bg-zinc-900 border-r border-zinc-800 py-6 px-3 gap-1',
        // Below md the sidebar is an overlay drawer opened from the header menu button.
        open ? 'flex fixed inset-y-0 left-0 z-40 overflow-y-auto md:static md:overflow-visible' : 'hidden md:flex',
      ].join(' ')}
    >
      <div className="mb-6 px-2">
        <span className="text-violet-400 font-bold text-lg tracking-tight">Chelless Admin</span>
        {ENV_LABEL && (
          <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold tracking-wide uppercase bg-zinc-800 text-amber-400 border border-zinc-700 align-middle">
            {ENV_LABEL}
          </span>
        )}
      </div>
      {NAV_ITEMS.map((item) => (
        <NavItem key={item.path} {...item} badge={item.path === '/experts' ? expertBacklogAlertCount ?? undefined : undefined} />
      ))}
      <p className="mt-4 mb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-zinc-600">
        Reports
      </p>
      {REPORT_NAV_ITEMS.map((item) => (
        <NavItem key={item.path} {...item} />
      ))}
    </nav>
  );
}
